// src/components/shared/InsightCaptureModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Lightbulb, Tag, Save } from 'lucide-react';
import { useProgressStore } from '../../stores/progressStore';

const InsightCaptureModal = ({ isOpen, onClose, context = {} }) => {
  const saveInsight = useProgressStore((state) => state.saveInsight);

  const [quote, setQuote] = useState('');
  const [personalNote, setPersonalNote] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);

  // Pre-populate from context
  useEffect(() => {
    if (context.selectedText) {
      setQuote(context.selectedText);
    }
  }, [context.selectedText]);

  // Available tags based on friendship themes
  const availableTags = [
    'Communication',
    'Boundaries',
    'Empathy',
    'Conflict Resolution',
    'Self-Awareness',
    'Trust',
    'Support',
    'Authenticity',
    'Growth Areas',
    'Strengths',
  ];

  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleSave = () => {
    if (!quote.trim() && !personalNote.trim()) {
      return; // Don't save empty insights
    }

    const insight = {
      moduleId: context.moduleId,
      moduleName: context.moduleName,
      toolId: context.toolId,
      toolName: context.toolName,
      section: context.section,
      quote: quote.trim(),
      personalNote: personalNote.trim(),
      tags: selectedTags,
    };

    saveInsight(insight);

    // Reset form
    setQuote('');
    setPersonalNote('');
    setSelectedTags([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl border border-white/10 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-b border-white/10 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Save Insight</h2>
              <p className="text-sm text-slate-300">
                {context.moduleName || context.toolName || 'Capture your learning moment'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Quote/Snippet */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Key Quote or Idea
            </label>
            <textarea
              value={quote}
              onChange={(e) => setQuote(e.target.value)}
              placeholder="Copy a quote or write down the key idea that resonated with you..."
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 resize-none"
              rows={3}
            />
          </div>

          {/* Personal Note */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Your Reflection
              <span className="ml-2 text-xs text-slate-500">(Optional)</span>
            </label>
            <textarea
              value={personalNote}
              onChange={(e) => setPersonalNote(e.target.value)}
              placeholder="Why does this resonate? How will you apply it? What does it mean for your friendships?"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 resize-none"
              rows={4}
            />
          </div>

          {/* Tags */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-3">
              <Tag className="w-4 h-4" />
              Tags
              <span className="text-xs text-slate-500">(Select all that apply)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {availableTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    selectedTags.includes(tag)
                      ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/25'
                      : 'bg-white/5 text-slate-400 hover:bg-white/10 border border-white/10'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-900/90 backdrop-blur border-t border-white/10 p-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!quote.trim() && !personalNote.trim()}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-slate-700 disabled:to-slate-700 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-500/25"
          >
            <Save className="w-4 h-4" />
            Save Insight
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsightCaptureModal;
